import React, { useCallback, useEffect, useRef, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import hero2 from "../../assets/Tradeplus_banner_2160px.avif";

import "../../styles/hero.css";

const ads = [
  { id: 1, type: "image", src: hero2, alt: "Special Offer 2" },
];

const Hero = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const videoRef = useRef(null);
  const timeoutRef = useRef(null);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % ads.length);
  }, []);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + ads.length) % ads.length);
  }, []);

  // Autoplay Logic
  useEffect(() => {
    const isVisible = !document.hidden;

    if (ads[currentIndex].type === "video" && isVisible) {
      try {
        videoRef.current?.play();
      } catch (err) {
        console.warn("Autoplay blocked:", err.message);
      }
    }

    clearTimeout(timeoutRef.current);

    if (isVisible && !isPaused) {
      timeoutRef.current = setTimeout(() => {
        handleNext();
      }, 4500);
    }

    return () => clearTimeout(timeoutRef.current);
  }, [currentIndex, isPaused, handleNext]);

  // Touch / Swipe Handling
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const handleTouchStart = (e) => {
    setIsPaused(true);
    touchStartX.current = e.changedTouches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    setIsPaused(false);
    touchEndX.current = e.changedTouches[0].clientX;
    handleSwipe();
  };

  const handleSwipe = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    const threshold = 40;

    if (distance > threshold) {
      handleNext(); // swipe left
    } else if (distance < -threshold) {
      handlePrev(); // swipe right
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  return (
    <section className="hero" id="hero">
      <div
        className="hero__container"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Banner Media Layer */}
        <div className="hero__media">
          {ads.map((ad, idx) => {
            const isActive = idx === currentIndex;
            return ad.type === "image" ? (
              <img
                key={ad.id}
                src={ad.src}
                alt={ad.alt || `Offer ${idx + 1}`}
                className={`hero__media-content ${isActive ? "active" : ""}`}
                loading={idx === 0 ? "eager" : "lazy"}
              />
            ) : (
              <video
                key={ad.id}
                ref={isActive ? videoRef : null}
                src={ad.src}
                className={`hero__media-content ${isActive ? "active" : ""}`}
                muted
                loop
                playsInline
                autoPlay
              />
            );
          })}
        </div>

        {/* Navigation Arrow Left */}
        <button
          className="hero__arrow hero__arrow--left"
          onClick={handlePrev}
          aria-label="Previous Slide"
        >
          <ChevronLeftIcon />
        </button>

        {/* Navigation Arrow Right */}
        <button
          className="hero__arrow hero__arrow--right"
          onClick={handleNext}
          aria-label="Next Slide"
        >
          <ChevronRightIcon />
        </button>

        {/* Pagination Dots */}
        <div className="hero__dots">
          {ads.map((_, idx) => (
            <button
              key={idx}
              className={`hero__dot ${idx === currentIndex ? "active" : ""}`}
              onClick={() => setCurrentIndex(idx)}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;